import { useTranslation } from "react-i18next";
import { StyleSheet, Text, View } from "react-native";

import { Button } from "../primitives/Button";
import { Dialog } from "../primitives/Dialog";
import { formatWhen } from "@heraion/shared";
import { useDateLocale } from "../primitives/DateFormatContext";
import type { UiTheme } from "../theme";

// The two questions a device draft can raise on opening (#336, useDraftResume):
//  - `newDraft`: «+ New» found a never-saved draft (program or plan) from
//    {{when}}: resume it, or start over (the draft is thrown away);
//  - `conflict`: an existing program changed on the server after the draft
//    was started: keep my draft, or use the updated version.
//  - `savedElsewhere` (S5): a Save met a newer version (412) and wrote
//    nothing. Same two answers, but keep my draft saves it over that version.
// No tap-out and no ✕. For the first two, neither answer writes and Android's
// back lands on the first, the one that loses nothing. For `savedElsewhere`,
// back is `onClose`: nothing written, the draft stays unsaved, and the next
// Save meets the 412 again. Its two buttons share one neutral look: the text
// says what each does.

export type DraftResumeDialogProps = {
  theme: UiTheme;
  kind: "newDraft" | "conflict" | "savedElsewhere";
  visible: boolean;
  /** When the draft was written (newDraft only). */
  savedAtMs?: number | null;
  /** Resume / keep my draft. */
  onKeep: () => void;
  /** Start over / use the updated version. */
  onDrop: () => void;
  /** Android's back, when it is not `onKeep` (savedElsewhere). */
  onClose?: () => void;
};

export function DraftResumeDialog({
  theme,
  kind,
  visible,
  savedAtMs,
  onKeep,
  onDrop,
  onClose,
}: DraftResumeDialogProps) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const styles = makeStyles(theme);
  const neutral = kind === "savedElsewhere";
  const when =
    savedAtMs != null ? formatWhen(savedAtMs, locale, { date: true, time: true }) : "";
  const copy =
    kind === "newDraft"
      ? {
          title: t("program.newDraft.title", { defaultValue: "A draft in progress" }),
          body: when
            ? t("program.newDraft.body", {
                when,
                defaultValue:
                  "A draft started on this device on {{when}} was never saved. Resume it, or start over and throw it away?",
              })
            : t("program.newDraft.bodyNoTime", {
                defaultValue:
                  "A draft started on this device was never saved. Resume it, or start over and throw it away?",
              }),
          keep: t("program.newDraft.resume", { defaultValue: "Resume" }),
          drop: t("program.newDraft.startOver", { defaultValue: "Start over" }),
        }
      : {
          title: t("program.draftConflict.title", { defaultValue: "The program has changed" }),
          body:
            kind === "savedElsewhere"
              ? t("program.savedElsewhere.body", {
                  defaultValue:
                    "It was changed elsewhere after you opened it. Nothing was saved. Keeping your draft replaces that version.",
                })
              : t("program.draftConflict.body", {
                  defaultValue:
                    "It was changed elsewhere after your draft was started. Keep your draft, or use the updated version? Nothing changes until you tap Save.",
                }),
          keep: t("program.draftConflict.keepDraft", { defaultValue: "Keep my draft" }),
          drop: t("program.draftConflict.useUpdated", {
            defaultValue: "Use the updated version",
          }),
        };
  return (
    <Dialog
      theme={theme}
      visible={visible}
      onRequestClose={onClose ?? onKeep}
      dismissOnBackdropPress={false}
      title={copy.title}
    >
      <Text style={styles.body}>{copy.body}</Text>
      <View style={styles.stack}>
        <Button theme={theme} variant={neutral ? "secondary" : "primary"} label={copy.keep} numberOfLines={2} onPress={onKeep} />
        <Button theme={theme} variant={neutral ? "secondary" : "danger"} label={copy.drop} numberOfLines={2} onPress={onDrop} />
      </View>
    </Dialog>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    body: { color: p.ink2, fontSize: ty.sizes.base },
    stack: { alignItems: "stretch", gap: s.s3 },
  });
}
